import type { ReactNode } from "react";
import { Navigate, Route, BrowserRouter, Routes } from "react-router-dom";
import type { PlatformApiClient } from "./api/PlatformApiClient";
import { PlatformClientProvider } from "./api/ClientContext";
import { PlatformAuthProvider } from "./auth/PlatformAuthContext";
import { RequirePlatformAuth } from "./auth/RequirePlatformAuth";
import { ImpersonationProvider } from "./auth/ImpersonationContext";
import { DemoModeBanner } from "./components/DemoModeBanner";
import { ImpersonationBanner } from "./components/ImpersonationBanner";
import { PlatformNav } from "./components/PlatformNav";
import { LoginPage } from "./pages/LoginPage";
import { MfaPage } from "./pages/MfaPage";
import { OverviewPage } from "./pages/overview/OverviewPage";
import { OnboardingWizardPage } from "./pages/onboarding/OnboardingWizardPage";
import { SystemHealthPage } from "./pages/health/SystemHealthPage";
import { LogsPage } from "./pages/logs/LogsPage";
import { QueuesPage } from "./pages/queues/QueuesPage";
import { MarketplaceOrdersPage } from "./pages/marketplace/MarketplaceOrdersPage";
import { MarketplaceOrderDetailPage } from "./pages/marketplace/MarketplaceOrderDetailPage";
import { TenantsListPage } from "./pages/tenants/TenantsListPage";
import { TenantDetailPage } from "./pages/tenants/TenantDetailPage";
import { ImpersonationViewPage } from "./pages/tenants/ImpersonationViewPage";
import { AuditLogPage } from "./pages/audit/AuditLogPage";
import { MyAccountPage } from "./pages/account/MyAccountPage";
import { GeneralSettingsPage } from "./pages/settings/GeneralSettingsPage";

function Shell({ children }: { children: ReactNode }) {
  return (
    <RequirePlatformAuth>
      <PlatformNav>{children}</PlatformNav>
    </RequirePlatformAuth>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/mfa" element={<MfaPage />} />

      <Route
        path="/"
        element={
          <Shell>
            <OverviewPage />
          </Shell>
        }
      />
      <Route
        path="/account"
        element={
          <Shell>
            <MyAccountPage />
          </Shell>
        }
      />
      <Route
        path="/tenants"
        element={
          <Shell>
            <TenantsListPage />
          </Shell>
        }
      />
      <Route
        path="/tenants/:tenantId"
        element={
          <Shell>
            <TenantDetailPage />
          </Shell>
        }
      />
      <Route
        path="/tenants/:tenantId/impersonate"
        element={
          <Shell>
            <ImpersonationViewPage />
          </Shell>
        }
      />
      <Route
        path="/onboarding"
        element={
          <Shell>
            <OnboardingWizardPage />
          </Shell>
        }
      />
      <Route
        path="/health"
        element={
          <Shell>
            <SystemHealthPage />
          </Shell>
        }
      />
      <Route
        path="/logs"
        element={
          <Shell>
            <LogsPage />
          </Shell>
        }
      />
      <Route
        path="/queues"
        element={
          <Shell>
            <QueuesPage />
          </Shell>
        }
      />
      <Route
        path="/marketplace/orders"
        element={
          <Shell>
            <MarketplaceOrdersPage />
          </Shell>
        }
      />
      <Route
        path="/marketplace/orders/:orderId"
        element={
          <Shell>
            <MarketplaceOrderDetailPage />
          </Shell>
        }
      />
      <Route
        path="/audit-log"
        element={
          <Shell>
            <AuditLogPage />
          </Shell>
        }
      />
      <Route
        path="/settings"
        element={
          <Shell>
            <GeneralSettingsPage />
          </Shell>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * `client` is injected here — the one place in this app that decides which
 * `PlatformApiClient` implementation is live (`main.tsx` passes `mockPlatformApiClient` or
 * `realPlatformApiClient`). Every page below reaches it via `usePlatformClient()`
 * (`api/ClientContext.tsx`), never by importing a concrete implementation directly.
 *
 * `ImpersonationProvider` wraps `AppRoutes` (not just the tenant pages) so
 * `ImpersonationBanner` can render on every route for as long as a "view as tenant" session
 * is active (§9.1) — see `auth/ImpersonationContext.tsx`'s doc comment.
 */
export function App({ client }: { client: PlatformApiClient }) {
  return (
    <PlatformClientProvider client={client}>
      <PlatformAuthProvider client={client}>
        <ImpersonationProvider>
          <BrowserRouter>
            <div className="sl-top-banners">
              <DemoModeBanner />
              <ImpersonationBanner />
            </div>
            <AppRoutes />
          </BrowserRouter>
        </ImpersonationProvider>
      </PlatformAuthProvider>
    </PlatformClientProvider>
  );
}
